import { type ComponentProps, useMemo } from 'react'

import { ButtonRadioGroup } from '@/components/common/Button'
import { InputSelect, InputSelectWithLabel, InputWithLabel } from '@/components/common/Input'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import {
  GALLERY_SIZE_LABELS,
  GALLERY_SIZE_SLUGS,
  type GallerySizeSlugType,
  isGalleryViewSizeable,
} from '@/defs/gallery'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch } from '@/store/hooks'
import { cn } from '@/utils/commonUtil'
// import { scrollToAnchor } from '@/utils/scrollUtil'

import { useGalleryDisplay } from '../../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryBounce } from '../../hooks/useGalleryBounce'

import styles from './GalleryControlsSize.module.scss'

type Props = {
  variant?: ComponentProps<typeof InputWithLabel>['variant']
}

const OPTIONS = GALLERY_SIZE_SLUGS.map((slug) => ({
  label: <IconWithLabel iconBefore={GALLERY_SIZE_LABELS[slug].icon}>{GALLERY_SIZE_LABELS[slug].label}</IconWithLabel>,
  value: slug,
}))

export const GalleryControlsSize = ({ variant }: Props) => {
  const dispatch = useReduxDispatch()
  const { size, view } = useGalleryDisplay()
  const isBouncing = useGalleryBounce({ editedKeys: ['size'] })

  const onSelect = (sizeNew: GallerySizeSlugType) => {
    /*
    TODO: backlogged: Re-enable scroll to the top-most visible card when clicked
    if (sizeNew !== size)
      requestAnimationFrame(() => scrollToAnchor({ id: 'gallery', ifNearTop: true, isSmooth: true }))
    */
    dispatch(galleryActions.setSize(sizeNew))
  }

  const options = useMemo(
    () =>
      GALLERY_SIZE_SLUGS.map((slug) => ({
        ariaLabel: GALLERY_SIZE_LABELS[slug].label,
        isSelected: slug === size,
        label: <Icon name={GALLERY_SIZE_LABELS[slug].icon} />,
        popover: (
          <IconWithLabel iconBefore={GALLERY_SIZE_LABELS[slug].icon}>{GALLERY_SIZE_LABELS[slug].label}</IconWithLabel>
        ),
        value: slug,
      })),
    [size],
  )

  if (!isGalleryViewSizeable(view)) return null

  return (
    <div className={cn(styles.container, { isBouncing, styles })}>
      {variant === 'horizontal' ? (
        <InputSelect onSelect={onSelect} options={OPTIONS} size='sm' value={size}>
          <InputSelectWithLabel label='Size' options={OPTIONS} size='sm' value={size} variant={variant} />
        </InputSelect>
      ) : (
        <InputWithLabel label='Size' variant={variant}>
          <ButtonRadioGroup
            as='button'
            onSelect={onSelect}
            options={options}
            shadow='none'
            size='sm'
            textStyle='none'
          />
        </InputWithLabel>
      )}
    </div>
  )
}
